import {downloadCSV} from "react-admin"
import jsonExport from "jsonexport/dist";
import {convertToHours, diffInMinutes, totalHours} from "../resources/activity/utils";
import moment from 'moment';
import {periodicityName} from "../resources/contract/constants";
import {CANCELED, MISSED, orderBillableHoursType, orderStatusName} from "../resources/order/constants";
import {activityTypeName} from "../resources/activity/constants";
import {groupBy, orderPrice, orderPricePerParticipant} from "./utils";
import {fullNamesListText, fullNameText} from "../components/FullNameField";

const removeAttachments = array => {
    if (!array) return null;
    return array.map(obj => {
        if (obj.hasOwnProperty("attachments")) {
            delete obj.attachments;
        }
        return obj;
    })
}

export const ContractsExporter = translate => contracts => {
    const preparedRecords = contracts.map(c => ({
        ...c,
        customer: {
            id: c.customer.id,
            name: c.customer.name,
            shortName: c.customer.shortName
        },
        contractor: {
            id: c.contractor.id,
            name: c.contractor.name,
            shortName: c.contractor.shortName
        },
    }));

    Exporter(translate, 'contracts')(preparedRecords);
};

export const EmployeesExporter = translate => employees => {
    const preparedRecords = employees.map(e => ({
        ...e,
        address: {
            ...e?.address
        },
    }));

    Exporter(translate, 'employees')(preparedRecords);
};

export const UsersExporter = translate => users => {
    const preparedRecords = users.map(u => ({
        ...u,
        employee: `${u?.employee?.firstName || ''} ${u?.employee?.lastName || ''}`,
    }));

    Exporter(translate, 'users')(preparedRecords);
};

export const OrdersExporter = translate => orders => {

    const preparedRecords = orders.map(o => {
        const duty = o?.duty || o?.oneTimeDuty
        const dutyAddress = o?.duty ? duty?.address?.address : duty?.address
        const activities = duty?.activities;
        const realizationDate = moment(o?.realizationDate).format('YYYY-MM-DD');
        const overrideHours = o?.overrideMinutes ? convertToHours(o?.overrideMinutes) : 0;
        const expectedHours = totalHours(activities);
        const workingHours = convertToHours(diffInMinutes(o?.startRealizationDateTime, o?.endRealizationDateTime));

        return {
            id: o?.id,
            order: o,
            orderNumber: o?.orderNumber,
            code: o?.code,
            realizationDate: realizationDate,
            status: orderStatusName(translate, o?.status),
            statusRaw: o?.status,
            zip: dutyAddress?.zip,
            city: dutyAddress?.city,
            street: dutyAddress?.street,
            number: dutyAddress?.number,
            assigneeName: fullNameText("")(o?.assignee),
            helperNames: fullNamesListText(o?.helpers),
            assignee: o?.assignee,
            helpers: o?.helpers,
            activities: activities ? activities.map(a => a.shortName).join(", ") : '',
            activityTypes: activities ? activities.map(a => activityTypeName(translate, o.overrideActivityType || a.type)).join(", ") :  activityTypeName(translate, o.overrideActivityType) || '',
            expectedHours: expectedHours,
            workingHours: workingHours,
            overrideHours: overrideHours,
            billableHoursType: orderBillableHoursType(translate, overrideHours, workingHours, expectedHours),
            bonusPercentage: o?.bonus,
            price: orderPrice(o),
        }
    });

    const sumArray = array => array.reduce((a, b) => a + b, 0);
    const skipStatuses = [CANCELED, MISSED]

    const flatWorkLogs = preparedRecords.flatMap(r => {
        const participants = [r.assignee, ...(r.helpers || [])].filter(Boolean);

        return participants.map(employee => ({
            ...r, currentEmployee: employee, employeeId: employee.id,
        }));
    });
    const grouped = groupBy(flatWorkLogs, "employeeId");

    const totals = Object.keys(grouped).map(employeeId => {
        const employeeRecords = grouped[employeeId];
        if (employeeRecords.length === 0) return {};

        const employee = employeeRecords[0].currentEmployee;
        const ordersToPay = employeeRecords.filter(r => !skipStatuses.includes(r.statusRaw));
        const sumPerParticipant = (order, hours) => {
            const numberOfParticipants = (order?.helpers?.length || 0) + (order?.assignee ? 1 : 0);
            return hours / (numberOfParticipants || 1);
        }

        const expectedHours = sumArray(ordersToPay.map(o => sumPerParticipant(o?.order, o?.expectedHours)));
        const workingHours = sumArray(ordersToPay.map(o => o?.workingHours));
        const expectedPayment = Math.round(sumArray(ordersToPay.map(o => orderPricePerParticipant(employee, o?.order, 0))) * 100) / 100;
        const payment = Math.round(sumArray(ordersToPay.map(o => orderPricePerParticipant(employee, o?.order, o?.workingHours))) * 100) / 100;

        return {
            employeeName: fullNameText("")(employee),

            baseHourlyRate: employee?.hourlyRates?.BASE,
            windowHourlyRate: employee?.hourlyRates?.WINDOW,
            laundryHourlyRate: employee?.hourlyRates?.LAUNDRY,
            commissionHourlyRate: employee?.hourlyRates?.COMMISSION,
            baseDuringCommissionDayHourlyRate: employee?.hourlyRates?.BASE_DURING_COMMISSION_DAY,
            bonusHourlyRate: employee?.bonusHourlyRate,
            expectedHours: expectedHours,
            workingHours: workingHours,
            expectedPayment: expectedPayment,
            payment: payment
        };
    });

    [...preparedRecords, ...totals]
        .map(r => {
            delete r.order;
            delete r.statusRaw;
            delete r.currentEmployee;
            delete r.assignee;
            delete r.helpers;
            return r;
        });

    DoubleExporter(translate, 'orders', preparedRecords, totals);
};


export const DutiesExporter = translate => duties => {
    const formatDate = date => {
        const momentDate = moment(date)
        return momentDate.isValid() ? momentDate.format('YYYY-MM-DD') : '';
    }

    const preparedRecords = duties.map(d => {
        const activities = d?.activities;
        const hours = totalHours(activities);
        return {
            id: d?.id,
            name: d?.name,
            startDate: formatDate(d?.startDate),
            periodicity: periodicityName(translate, d?.periodicity),
            zip: d?.address?.address?.zip,
            city: d?.address?.address?.city,
            street: d?.address?.address?.street,
            number: d?.address?.address?.number,
            minOrderDate: formatDate(d?.minOrderDate),
            maxOrderDate: formatDate(d?.maxOrderDate),
            ordersCount: d?.ordersCount,
            activities: activities ? activities.map(a => a.shortName).join(",") : '',
            hours: hours,
        }
    });

    Exporter(translate, 'duties')(preparedRecords);
};

const createCsv = (records, header, filename) => {
    jsonExport(removeAttachments(records), {
        fillGaps: true,
        mapHeaders: header
    }, (err, csv) => {
        if (err) {
            console.log("Error exporting groups: " + JSON.stringify(err));
        } else {
            const BOM = '\uFEFF'
            downloadCSV(`${BOM} ${csv}`, filename)
        }
    });
}

export const Exporter = (translate, resource) => records => {
    createCsv(
        records,
        header => translate(`resources.${resource}.fields.${header}`),
        translate(`resources.${resource}.name`, {smart_count: 2})
    );
}

export const DoubleExporter = (translate, resource, records, sumRecords) => {
    createCsv(
        records,
        header => translate(`resources.${resource}.fields.${header}`),
        translate(`resources.${resource}.name`, {smart_count: 2})
    );
    createCsv(
        sumRecords,
        header => translate(`resources.${resource}.fields.${header}`),
        translate(`resources.${resource}.sumName`)
    );
}
